import { Role } from 'auth';
import get from 'lodash/get';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { commonPaths } from '@/modules/common/routes.tsx';
import { SignUpPagesWrapper } from '@/modules/auth/components/SignUpPagesWrapper.tsx';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { OnboardingNavigation } from '@/modules/entity/components/OnboardingNavigation.tsx';
import { useEntityItemUncachedQuery } from '@/modules/entity/queries/entity.tsx';
import { entityPaths } from '@/modules/entity/routes.tsx';
import { ShareholderDetails } from '@/modules/entity/types/EntityItemResponse.ts';
import { addedShareholdersRecursive } from '@/modules/entity/utils/stepper/generateStepsMappedByField.ts';
import {
  associationRoad,
  companyRoad,
  individualRoad,
  partnershipRoad,
  smsfRoad,
  trustRoad,
} from '@/modules/entity/utils/stepper/roads.ts';
import { OnboardingRoadItem } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { Spinner } from 'ui';
import { EntityType } from 'api';

export const allRoads: Record<EntityType, OnboardingRoadItem[]> = {
  individual: individualRoad,
  company: companyRoad,
  trust: trustRoad,
  partnership: partnershipRoad,
  association: associationRoad,
  smsf: smsfRoad,
};

const SignupEntityPage = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const step = params.get('step') ?? '0';
  const [currentStep, setCurrentStep] = useState(step && !isNaN(+step) ? +step : 0);
  const { id: entity_id = 0 } = useParams();
  const { data, isLoading } = useEntityItemUncachedQuery(
    +entity_id,
    entity_id === 0 || isNaN(+entity_id),
  );

  const roads = useMemo<OnboardingRoadItem[]>(() => {
    if (!data?.entity.type) return [];

    let items = allRoads?.[data?.entity.type as EntityType];

    items = items?.filter(item => {
      if (item.entity_type_prefix && item.person_details_key) {
        const shareholders = (
          get(
            data?.entity,
            `${item.entity_type_prefix}.${item.person_details_key}`,
            [],
          ) as ShareholderDetails[]
        )?.filter(item => !item.same_as_index);

        if (
          item?.optionalStepsProps?.field_name &&
          item?.optionalStepsProps?.field_name !== item.person_details_key
        ) {
          const subShareholders = shareholders
            .map((subShareholder: ShareholderDetails) =>
              get(subShareholder, item?.optionalStepsProps?.field_name ?? '', [])?.map(
                value => value,
              ),
            )
            .flat() as ShareholderDetails[];

          return !!subShareholders.length;
        }

        return !!shareholders?.length;
      }
      return true;
    });
    const entity_type_prefix = items?.[0]?.entity_type_prefix ?? '';

    //Add trustee Recursive
    const newTrusteeItems: OnboardingRoadItem[] = [];
    const trustee: ShareholderDetails[] = data?.entity?.[entity_type_prefix]?.trustee || [];
    addedShareholdersRecursive({
      shareholders: trustee,
      newRoadItems: newTrusteeItems,
      entity_type_prefix: entity_type_prefix + '.trustee',
      navName: 'Trustee',
    });
    const trusteeIndex = items.findIndex(item => item.person_details_key === 'trustee');

    if (trusteeIndex !== -1 && newTrusteeItems.length > 0) {
      items.splice(trusteeIndex + 1, 0, ...newTrusteeItems);
    }

    //Add beneficiaries Recursive
    const newBeneficiariesItems: OnboardingRoadItem[] = [];
    const beneficiaries: ShareholderDetails[] =
      data?.entity?.[entity_type_prefix]?.beneficiaries || [];
    addedShareholdersRecursive({
      shareholders: beneficiaries,
      newRoadItems: newBeneficiariesItems,
      entity_type_prefix: entity_type_prefix + '.beneficiaries',
      navName: 'Beneficiary',
    });
    const beneficiaryIndex = items.findIndex(item => item.person_details_key === 'beneficiaries');

    if (beneficiaryIndex !== -1 && newBeneficiariesItems.length > 0) {
      items.splice(beneficiaryIndex + 1, 0, ...newBeneficiariesItems);
    }

    //Add settlors Recursive
    const newSettlorsItems: OnboardingRoadItem[] = [];
    const settlors: ShareholderDetails[] = data?.entity?.[entity_type_prefix]?.settlors || [];
    addedShareholdersRecursive({
      shareholders: settlors,
      newRoadItems: newSettlorsItems,
      entity_type_prefix: entity_type_prefix + '.settlors',
      navName: 'Settlor',
    });
    const settlorIndex = items.findIndex(item => item.person_details_key === 'settlors');

    if (settlorIndex !== -1 && newSettlorsItems.length > 0) {
      items.splice(settlorIndex + 1, 0, ...newSettlorsItems);
    }

    //Add shareholders Recursive
    const newShareholdersItems: OnboardingRoadItem[] = [];
    const shareholders: ShareholderDetails[] =
      data?.entity?.[entity_type_prefix]?.shareholders || [];
    addedShareholdersRecursive({
      shareholders: shareholders,
      newRoadItems: newShareholdersItems,
      entity_type_prefix: entity_type_prefix + '.shareholders',
      navName: 'Shareholder',
    });
    const shareholderIndex = items.findIndex(item => item.person_details_key === 'shareholders');

    if (shareholderIndex !== -1 && newShareholdersItems.length > 0) {
      items.splice(shareholderIndex + 1, 0, ...newShareholdersItems);
    }

    return items;
  }, [data]);

  const {
    label,
    title,
    allowSkip,
    optionalStepsProps,
    entity_type_prefix,
    person_details_key,
    component: Component,
    conditionCallback,
    navName,
  } = roads?.[currentStep] ?? {};
  const getSubStep = useCallback((step: number) => +(params.get(`sub_step${step}`) ?? 0), [params]);
  const getSubStepRoad = useCallback(
    (step: number, subStep: number) => +(params.get(`sub_step_road${step}_${subStep}`) ?? 0),
    [params],
  );
  const setSubStep = useCallback(
    (step: number, value: number) => params.set(`sub_step${step}`, String(value)),
    [params],
  );
  const setSubStepRoad = useCallback(
    (step: number, subStep: number, value: number) =>
      params.set(`sub_step_road${step}_${subStep}`, String(value)),
    [params],
  );
  const delSubStep = useCallback((step: number) => params.delete(`sub_step${step}`), [params]);
  const delSubStepRoad = useCallback(
    (step: number, subStep: number) => params.delete(`sub_step_road${step}_${subStep}`),
    [params],
  );

  const handlePrevStep = useCallback(() => {
    const subStep = getSubStep(currentStep);
    const subStepRoad = getSubStepRoad(currentStep, subStep);

    if (currentStep === 0) {
      params.forEach((_, key) => {
        if (key.toLowerCase().includes('step')) {
          params.delete(key);
        }
      });
      navigate(
        { pathname: entityPaths.static.entityList, search: params.toString() },
        {
          replace: false,
        },
      );
      // navigate(
      //   { pathname: entityPaths.static.abnLookupEntity, search: params.toString() },
      //   { state: { entityId: +entity_id } },
      // );
      return;
    }

    if (subStepRoad && !isNaN(+subStepRoad) && +subStepRoad > 0) {
      setSubStepRoad(currentStep, subStep, subStepRoad - 1);
      setParams(params, { replace: false });
      return;
    }

    if (subStep && !isNaN(+subStep) && +subStep > 0) {
      setSubStep(currentStep, subStep - 1);
      delSubStepRoad(currentStep, subStep);
      setParams(params, { replace: false });
      return;
    }

    const newStep = currentStep - 1;

    params.set('step', String(newStep));
    delSubStepRoad(currentStep, subStep);
    delSubStep(currentStep);
    setCurrentStep(newStep);
    setParams(params, { replace: false });
  }, [
    currentStep,
    delSubStep,
    delSubStepRoad,
    getSubStep,
    getSubStepRoad,
    navigate,
    params,
    setParams,
    setSubStep,
    setSubStepRoad,
  ]);

  const handleNextStep = useCallback(() => {
    const newStep = currentStep + 1;
    params.set('step', String(newStep));
    delSubStep(newStep);
    delSubStepRoad(newStep, 0);
    const subStep = getSubStep(currentStep);
    delSubStepRoad(currentStep, subStep);
    delSubStep(currentStep);
    setParams(params, { replace: false });

    setCurrentStep(newStep);
  }, [currentStep, delSubStep, delSubStepRoad, getSubStep, params, setParams]);

  const handleGoToEntitiesList = useCallback(() => {
    params.forEach((_, key) => {
      //ToDo: need del all params with steps
      if (key.toLowerCase().includes('step')) {
        params.delete(key);
      }
    });
    setParams(params, { replace: false });
    navigate(
      { pathname: entityPaths.static.entityList, search: params.toString() },
      {
        replace: false,
      },
    );
  }, [navigate, params]);

  useEffect(() => {
    step && setCurrentStep(+step);
  }, [step]);

  if (!isLoading && !data?.entity) {
    return <Navigate to={commonPaths.static.notFound} />;
  }
  return (
    <SignUpPagesWrapper
      sidebar={<OnboardingNavigation roads={roads} />}
      backAction={{ onClick: handlePrevStep }}
      mainAction={{
        name: 'Back to entities list',
        onClick: handleGoToEntitiesList,
      }}
      className="justify-start"
    >
      {/*  */}
      <div className="max-w-[660px] w-full flex-1 flex flex-col items-center justify-center px-6">
        {isLoading ? (
          <Spinner size="lg" />
        ) : !Component ? (
          <div className="text-lg text-gray-600 font-bold">
            The component was not found for this step :(
          </div>
        ) : (
          <Component
            moveForward={handleNextStep}
            allowSkip={allowSkip}
            options={optionalStepsProps?.options || []}
            subroad={optionalStepsProps?.subroad || []}
            title={optionalStepsProps?.title || title || ''}
            entity_type_prefix={entity_type_prefix || ''}
            field_name={optionalStepsProps?.field_name || ''}
            index={optionalStepsProps?.index}
            description={optionalStepsProps?.description}
            person_details_key={person_details_key}
            conditionCallback={conditionCallback}
            label={label}
            navName={navName}
            roads={roads}
          />
        )}
      </div>
    </SignUpPagesWrapper>
  );
};

export default withPrivateAccess(SignupEntityPage, Role.SR_INVESTOR);
